/**
 * 视频管理 — 按 Listing 分组展示视频资产
 */

import { authFetch } from './api.js';
import { showToast, formatDate, formatNumber, renderBlockError, renderPagination } from './utils.js';
import { registerViewLoader } from './navigation.js';
import { getVideoManagerListingId, setVideoManagerListingId } from './state.js';

const VIDEO_PAGE_SIZE = 30;
const VIDEO_PAGE_PARAM = 'videoPage';
let groupedData = [];
let pagination = { page: 1, pageSize: VIDEO_PAGE_SIZE, total: 0 };
let expandedListings = new Set();
let _returnTo = 'listings';
let initialized = false;
let controlsBound = false;

function parseMetadata(value) {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
}

function clearListingIdFromUrl() {
  const u = new URL(window.location.href);
  if (!u.searchParams.has('listingId')) return;
  u.searchParams.delete('listingId');
  u.searchParams.delete(VIDEO_PAGE_PARAM);
  history.replaceState(null, '', u.pathname + u.search + u.hash);
}

export function normalizeVideoPage(value) {
  const page = Number.parseInt(String(value || ''), 10);
  return Number.isFinite(page) && page > 0 ? page : 1;
}

function readVideoPageFromUrl() {
  return normalizeVideoPage(new URLSearchParams(window.location.search).get(VIDEO_PAGE_PARAM));
}

function writeVideoStateToUrl({ page, listingId } = {}) {
  const u = new URL(window.location.href);
  if (page !== undefined) u.searchParams.set(VIDEO_PAGE_PARAM, String(normalizeVideoPage(page)));
  if (listingId !== undefined) {
    if (listingId) u.searchParams.set('listingId', String(listingId));
    else u.searchParams.delete('listingId');
  }
  history.replaceState(null, '', u.pathname + u.search + u.hash);
}

function writeVideoPageToUrl(page) {
  writeVideoStateToUrl({ page });
}

function resolveListingFilter() {
  const urlId = new URLSearchParams(window.location.search).get('listingId');
  if (urlId) {
    const id = parseInt(urlId, 10);
    setVideoManagerListingId(id);
    return id;
  }
  return getVideoManagerListingId();
}

export function buildVideoAssetsUrl({ page = 1, listingId = null, cacheKey = Date.now() } = {}) {
  const params = new URLSearchParams({
    includePending: '1',
    page: String(normalizeVideoPage(page)),
    pageSize: String(VIDEO_PAGE_SIZE),
    _t: String(cacheKey),
  });
  if (listingId) params.set('listingId', String(listingId));
  return `/api/video/assets?${params.toString()}`;
}

function buildApiUrl(page = readVideoPageFromUrl()) {
  const listingId = resolveListingFilter();
  return buildVideoAssetsUrl({ page, listingId });
}

function videoUrl(video) {
  return `/api/video/assets/${encodeURIComponent(video.id)}/file`;
}

function getGroupKey(group) {
  if (group.listing_id != null) return `listing:${group.listing_id}`;
  return 'unbound';
}

function actualVideoCost(video) {
  if (video?.status !== 'completed') return 0;
  return Math.max(0, Number(video.cost_tokens || 0) || 0);
}

function estimatedVideoCost(video, meta = null) {
  const data = meta || parseMetadata(video?.metadata);
  return Math.max(0, Number(video?.cost_tokens || data?.costTokens || 0) || 0);
}

function compactTimestamp(value) {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return '';
  const pad = (num) => String(num).padStart(2, '0');
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}_${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
}

function videoDisplayName(video, meta = null) {
  const data = meta || parseMetadata(video?.metadata);
  if (data.displayName) return data.displayName;
  if (data.filename) return String(data.filename).replace(/\.[^.]+$/, '');
  const inputMode = data.inputMode || data.input?.inputMode || '';
  const modeLabels = { single: '图生视频', firstLast: '首尾帧', multi: '多图参考' };
  const label = modeLabels[inputMode] || '图生视频';
  const stamp = compactTimestamp(video?.created_at || video?.createdAt || data.generatedAt);
  return stamp ? `${label}_${stamp}` : label;
}

function groupVideos(videos = []) {
  const map = new Map();
  for (const video of videos) {
    const key = video.listing_id != null ? `listing:${video.listing_id}` : 'unbound';
    if (!map.has(key)) {
      const isUnbound = video.listing_id == null;
      map.set(key, {
        listing_id: video.listing_id,
        job_id: isUnbound ? null : video.job_id,
        is_unbound: isUnbound,
        listing_name: isUnbound ? '未绑定 Listing' : (video.listing_name || `Listing #${video.listing_id}`),
        listing_title: isUnbound ? '直接上传图片生成的视频，未归属到具体 Listing。' : (video.listing_title || ''),
        videos: [],
      });
    }
    map.get(key).videos.push(video);
  }
  const groups = [...map.values()];
  groups.forEach((group) => {
    group.videos.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    group.completed_count = group.videos.filter((item) => item.status === 'completed' && item.file_url).length;
    group.inflight_count = group.videos.filter((item) => ['pending', 'processing'].includes(item.status)).length;
    group.failed_count = group.videos.filter((item) => item.status === 'failed').length;
    group.cost_tokens = group.videos.reduce((sum, item) => sum + actualVideoCost(item), 0);
    group.last_video_at = group.videos[0]?.created_at || null;
  });
  groups.sort((a, b) => new Date(b.last_video_at || 0).getTime() - new Date(a.last_video_at || 0).getTime());
  return groups;
}

export function initVideoManager() {
  if (!initialized) {
    initialized = true;
    registerViewLoader('video-manager', () => {
      const urlListingId = new URLSearchParams(window.location.search).get('listingId');
      if (!urlListingId) {
        setVideoManagerListingId(null);
        clearListingIdFromUrl();
      }
      return refreshVideos();
    });
  }

  if (controlsBound || !document.getElementById('view-video-manager')) return;
  controlsBound = true;

  document.getElementById('video-manager-back-btn')?.addEventListener('click', () => {
    setVideoManagerListingId(null);
    clearListingIdFromUrl();
    import('./navigation.js').then(({ switchView }) => switchView(_returnTo));
  });
  document.getElementById('vm-refresh-btn')?.addEventListener('click', () => refreshVideos());
  bindContainerEvents();
}

export function openVideoManager(listingId, returnTo = 'listings') {
  _returnTo = returnTo;
  setVideoManagerListingId(listingId);
  writeVideoStateToUrl({ page: 1, listingId });
  import('./navigation.js').then(({ switchView }) => switchView('video-manager'));
}

async function refreshVideos(page = readVideoPageFromUrl()) {
  const requestedPage = normalizeVideoPage(page);
  const loading = document.getElementById('vm-videos-loading');
  const empty = document.getElementById('vm-videos-empty');
  const container = document.getElementById('vm-videos-container');
  const subtitle = document.getElementById('video-manager-subtitle');
  if (loading) loading.hidden = false;
  if (empty) empty.hidden = true;
  if (container) container.replaceChildren();
  if (subtitle) subtitle.textContent = '加载中...';

  try {
    const resp = await authFetch(buildApiUrl(requestedPage));
    if (!resp.ok) throw new Error(`Failed to load videos (HTTP ${resp.status})`);
    const data = await resp.json();
    groupedData = groupVideos(data.videos || []);
    pagination = {
      page: normalizeVideoPage(data.page || requestedPage),
      pageSize: Number(data.pageSize) || VIDEO_PAGE_SIZE,
      total: Math.max(0, Number(data.total) || 0),
    };
    const lastPage = Math.max(1, Math.ceil(pagination.total / pagination.pageSize));
    if (pagination.total > 0 && pagination.page > lastPage) {
      writeVideoPageToUrl(lastPage);
      return refreshVideos(lastPage);
    }
    if (loading) loading.hidden = true;
    if (!groupedData.length) {
      updateEmptyState();
      renderVideoPagination();
      return;
    }
    const filterId = resolveListingFilter();
    if (filterId || groupedData.length === 1) {
      groupedData.forEach((group) => expandedListings.add(getGroupKey(group)));
    }
    renderGroups();
    renderSubtitle();
    renderVideoPagination();
  } catch (err) {
    console.error('[VideoManager] Refresh failed:', err);
    if (loading) loading.hidden = true;
    if (empty) {
      empty.hidden = false;
      renderBlockError(empty, `加载失败: ${err.message}`);
    }
    if (subtitle) subtitle.textContent = '加载失败';
    showToast('加载视频列表失败', 'error');
  }
}

function updateEmptyState() {
  const empty = document.getElementById('vm-videos-empty');
  const subtitle = document.getElementById('video-manager-subtitle');
  if (empty) empty.hidden = false;
  const listingId = resolveListingFilter();
  if (subtitle) {
    if (!listingId) {
      subtitle.textContent = '暂无视频资产';
      return;
    }
    subtitle.replaceChildren(
      document.createTextNode(`Listing #${listingId} 下暂无视频 · `),
      createVideoSuiteLink(listingId, '去生成视频'),
    );
  }
}

function renderSubtitle() {
  const subtitle = document.getElementById('video-manager-subtitle');
  if (!subtitle) return;
  const totals = groupedData.reduce((acc, group) => {
    acc.videos += group.videos.length;
    acc.completed += group.completed_count || 0;
    acc.inflight += group.inflight_count || 0;
    acc.credits += group.cost_tokens || 0;
    if (group.is_unbound) acc.unbound += 1;
    else acc.listings += 1;
    return acc;
  }, { videos: 0, completed: 0, inflight: 0, credits: 0, listings: 0, unbound: 0 });
  const inflight = totals.inflight ? ` (+${totals.inflight} 生成中)` : '';
  const groupLabel = [
    totals.listings ? `${totals.listings} 个 Listing` : '',
    totals.unbound ? '未绑定 Listing' : '',
  ].filter(Boolean).join(' · ') || '视频资产';
  const pageLabel = pagination.total > totals.videos
    ? ` · 第 ${pagination.page} 页 / 共 ${pagination.total} 条`
    : ` · 共 ${totals.videos} 条`;
  subtitle.textContent = `${groupLabel} · ${totals.completed} 条视频${inflight}${pageLabel} · LinkFox ${formatNumber(totals.credits)} 算力`;
}

function renderVideoPagination() {
  renderPagination('vm-videos-pagination', pagination.total, pagination.page, pagination.pageSize, (nextPage) => {
    writeVideoPageToUrl(nextPage);
    refreshVideos(nextPage);
  });
}

function renderGroups() {
  const container = document.getElementById('vm-videos-container');
  if (!container) return;
  const empty = document.getElementById('vm-videos-empty');
  if (empty) empty.hidden = true;
  container.replaceChildren(...groupedData.map(createVideoGroup));
}

function createVideoSuiteLink(listingId, label) {
  const link = document.createElement('a');
  link.className = 'video-mini-link';
  link.href = listingId ? `/video-suite.html?fromListing=${encodeURIComponent(listingId)}` : '/video-suite.html';
  link.textContent = label;
  return link;
}

function createVideoTextElement(tagName, className, text = '') {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  element.textContent = text;
  return element;
}

function createVideoGroup(group, idx) {
  const key = getGroupKey(group);
  const expanded = expandedListings.has(key);
  const countLabel = group.inflight_count
    ? `${group.completed_count} 条 (+${group.inflight_count} 生成中)`
    : `${group.completed_count} 条`;
  const lastDate = group.last_video_at ? formatDate(group.last_video_at) : '';
  const article = createVideoTextElement('article', 'vm-listing-card');
  const header = createVideoTextElement('div', 'vm-listing-header');
  header.dataset.groupIdx = String(idx);
  const chevron = createVideoTextElement('span', `vm-chevron${expanded ? ' is-expanded' : ''}`, '›');
  const main = createVideoTextElement('div', 'vm-listing-main');
  main.appendChild(createVideoTextElement('div', 'vm-listing-title', group.listing_name || '未关联 Listing'));
  if (group.listing_title) main.appendChild(createVideoTextElement('div', 'vm-listing-subtitle', group.listing_title));
  header.append(chevron, main);
  if (lastDate) header.appendChild(createVideoTextElement('span', 'vm-listing-date', lastDate));
  header.appendChild(createVideoTextElement('span', 'vm-count-badge', countLabel));
  article.appendChild(header);

  if (expanded) {
    const body = createVideoTextElement('div', 'vm-listing-body');
    const toolbar = createVideoTextElement('div', 'vm-group-toolbar');
    toolbar.append(
      createVideoTextElement('span', '', `已消耗 LinkFox ${formatNumber(group.cost_tokens || 0)} 算力`),
      createVideoSuiteLink(group.listing_id, '继续生成'),
    );
    const grid = createVideoTextElement('div', 'vm-video-grid');
    grid.replaceChildren(...group.videos.map(createVideoCard));
    body.append(toolbar, grid);
    article.appendChild(body);
  }
  return article;
}

function createVideoCard(video) {
  const meta = parseMetadata(video.metadata);
  const input = meta.input || {};
  const prompt = video.prompt || meta.prompt || '';
  const status = video.status || '';
  const modeLabels = { single: '图生视频', firstLast: '首尾帧', multi: '多图参考' };
  const sourceLabels = { listing_assets: 'Listing 套图', upload: '本地上传' };
  const detail = [
    video.model,
    video.duration ? `${video.duration}s` : '',
    video.resolution,
    modeLabels[input.inputMode] || input.inputMode || '',
    sourceLabels[input.referenceSource] || input.referenceSource || '',
  ].filter(Boolean).join(' · ');
  const statusText = status === 'failed' ? '失败' : (status === 'completed' ? '' : '处理中');
  const actualCost = actualVideoCost(video);
  const estimatedCost = estimatedVideoCost(video, meta);
  const displayName = videoDisplayName(video, meta);
  const costLabel = actualCost > 0
    ? `LinkFox ${formatNumber(actualCost)} 算力`
    : (estimatedCost > 0 && status !== 'completed'
      ? `预计 LinkFox ${formatNumber(estimatedCost)} 算力${status === 'failed' ? '，失败未计入' : ''}`
      : '');
  const card = createVideoTextElement('article', 'vm-video-card');
  const preview = createVideoTextElement('div', 'vm-video-preview');
  if (video.file_url && status === 'completed') {
    const player = document.createElement('video');
    player.className = 'vm-video-player';
    player.src = videoUrl(video);
    player.controls = true;
    player.playsInline = true;
    player.preload = 'metadata';
    preview.appendChild(player);
  } else {
    preview.appendChild(createVideoTextElement('div', 'vm-video-empty', status === 'completed' ? '视频文件缺失' : '处理中'));
  }
  if (statusText) preview.appendChild(createVideoTextElement('span', `vm-status-badge${status === 'failed' ? ' failed' : ''}`, statusText));
  const body = createVideoTextElement('div', 'vm-video-body');
  body.append(
    createVideoTextElement('div', 'vm-video-meta', displayName),
    createVideoTextElement('div', 'vm-video-meta', detail || '视频任务'),
    createVideoTextElement('div', 'vm-video-meta', formatDate(video.created_at || video.createdAt)),
  );
  if (costLabel) body.appendChild(createVideoTextElement('div', 'vm-video-credit', costLabel));
  if (prompt) {
    const promptNode = createVideoTextElement('div', 'vm-video-prompt', prompt);
    promptNode.title = prompt;
    body.appendChild(promptNode);
  }
  if (video.error_message) body.appendChild(createVideoTextElement('div', 'vm-video-error', video.error_message));
  card.append(preview, body);
  return card;
}

function bindContainerEvents() {
  const container = document.getElementById('vm-videos-container');
  if (!container || container.dataset.vmBound === '1') return;
  container.dataset.vmBound = '1';
  container.addEventListener('error', (event) => {
    const video = event.target.closest('video.vm-video-player');
    if (!video || !container.contains(video)) return;
    const card = video.closest('.vm-video-preview');
    if (!card || card.querySelector('.vm-video-missing')) return;
    video.replaceWith(Object.assign(document.createElement('div'), {
      className: 'vm-video-empty vm-video-missing',
      textContent: '视频文件缺失',
    }));
  }, true);
  container.addEventListener('click', (event) => {
    if (event.target.closest('video, a, button')) return;
    const header = event.target.closest('.vm-listing-header');
    if (!header) return;
    const group = groupedData[parseInt(header.dataset.groupIdx, 10)];
    if (!group) return;
    const key = getGroupKey(group);
    if (expandedListings.has(key)) expandedListings.delete(key);
    else expandedListings.add(key);
    renderGroups();
  });
}
